import type { Component } from "../../types";
import {
  ensureResponsivePositions,
  toIdMap,
} from "./editor-store-helpers";
import type {
  EditorImmerGetState,
  EditorImmerSetState,
} from "./editor-store-types";

export const MAX_HISTORY_ENTRIES = 50;

export interface HistorySlice {
  history: {
    past: Component[][];
    present: Component[];
    future: Component[][];
  };
  _undoBatchActive: boolean;
  _undoBatchSnapshot: Component[] | null;
  undo: () => void;
  redo: () => void;
  pushToHistory: () => void;
  clearHistory: () => void;
  startUndoBatch: (label: string) => void;
  endUndoBatch: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
}

export const historyInitialState: Pick<
  HistorySlice,
  "history" | "_undoBatchActive" | "_undoBatchSnapshot"
> = {
  history: {
    past: [],
    present: [],
    future: [],
  },
  _undoBatchActive: false,
  _undoBatchSnapshot: null,
};

type HistorySliceState = HistorySlice & {
  components: Component[];
  componentMap: Map<string, Component>;
  currentPageId?: string;
  pageComponents: Map<string, Component[]>;
  pageHistoryMap: Map<
    string,
    { past: Component[][]; present: Component[]; future: Component[][] }
  >;
  isModified: boolean;
};

function trimPastHistory(history: Component[][]): Component[][] {
  return history.length > MAX_HISTORY_ENTRIES
    ? history.slice(-MAX_HISTORY_ENTRIES)
    : history;
}

function syncCurrentPageSnapshot(state: HistorySliceState): void {
  if (!state.currentPageId) {
    return;
  }

  state.pageComponents.set(state.currentPageId, [...state.components]);
  state.pageHistoryMap.set(state.currentPageId, state.history);
}

export function createHistorySlice<TState extends HistorySliceState>(
  set: EditorImmerSetState<TState>,
  get: EditorImmerGetState<TState>,
): HistorySlice {
  return {
    ...historyInitialState,

    undo: () => {
      set((state) => {
        if (state.history.past.length === 0) {
          return;
        }

        const previous = state.history.past[state.history.past.length - 1];
        const restoredComponents = ensureResponsivePositions(previous);

        state.history.future.unshift(state.history.present);
        state.history.past = state.history.past.slice(
          0,
          state.history.past.length - 1,
        );
        state.history.present = restoredComponents;
        state.components = [...restoredComponents];
        state.componentMap = toIdMap(restoredComponents);
        state.isModified = true;

        syncCurrentPageSnapshot(state);
      });
    },

    redo: () => {
      set((state) => {
        if (state.history.future.length === 0) {
          return;
        }

        const next = state.history.future[0];
        const restoredComponents = ensureResponsivePositions(next);

        state.history.past = trimPastHistory([
          ...state.history.past,
          state.history.present,
        ]);
        state.history.future = state.history.future.slice(1);
        state.history.present = restoredComponents;
        state.components = [...restoredComponents];
        state.componentMap = toIdMap(restoredComponents);
        state.isModified = true;

        syncCurrentPageSnapshot(state);
      });
    },

    pushToHistory: () => {
      if (get()._undoBatchActive) {
        return;
      }

      set((state) => {
        state.history.past = trimPastHistory([
          ...state.history.past,
          [...state.history.present],
        ]);
        state.history.present = [...state.components];
        state.history.future = [];

        syncCurrentPageSnapshot(state);
      });
    },

    clearHistory: () => {
      set((state) => {
        state.history = {
          past: [],
          present: [...state.components],
          future: [],
        };

        syncCurrentPageSnapshot(state);
      });
    },

    startUndoBatch: (_label: string) => {
      set((state) => {
        state._undoBatchActive = true;
        state._undoBatchSnapshot = [...state.components];
      });
    },

    endUndoBatch: () => {
      const snapshot = get()._undoBatchSnapshot;

      set((state) => {
        if (snapshot) {
          state.history.past = trimPastHistory([...state.history.past, snapshot]);
          state.history.present = [...state.components];
          state.history.future = [];
        }

        state._undoBatchActive = false;
        state._undoBatchSnapshot = null;

        syncCurrentPageSnapshot(state);
      });
    },

    canUndo: () => get().history.past.length > 0,
    canRedo: () => get().history.future.length > 0,
  };
}
